import { forwardRef, useCallback, useMemo } from "react";
import { useQueryParams } from "../../dataAccess/QueryParams";
import {
  Category,
  CategoryResponse,
  useCategories,
} from "../../dataAccess/api/categories";
import Filter, {
  FilterCommonSettings,
  FilterCommonProps,
  Option,
  ExpandedByDefaultOption,
} from "../ui/Filter";
import { PublicComponentProps } from "./PublicComponentProps";
import { GridSettings } from "./Grid";

export interface Settings extends FilterCommonSettings {
  isOpenOnMobile?: ExpandedByDefaultOption;
  isOpenOnDesktop?: ExpandedByDefaultOption;
}
// TODO: remove "any" type in the next lines
interface Props
  extends Pick<FilterCommonProps, "mobile">,
    PublicComponentProps<Settings, any, any, GridSettings> {
  categories?: CategoryResponse;
}

const CategoriesFilter = forwardRef(
  ({ categories, settings, mobile }: Props, ref) => {
    const { category_ids } = useQueryParams();
    const { loadingCategoryList } = useCategories();
    const categoriesParents = categories?.filter(({ parent }) => !parent);
    const categoryToOption = useCallback(
      ({ id: value, name: label, numberOfItems }: Category): Option => ({
        value: String(value),
        label,
        detailsLabel: settings.showNumberOfResults
          ? String(numberOfItems || "")
          : "",
        subOptions: categories
          ?.filter(({ parent }) => parent === String(value))
          .map(categoryToOption),
      }),
      [settings, categories],
    );

    const expandedSettings = useMemo(
      () => (mobile ? settings.isOpenOnMobile : settings.isOpenOnDesktop),
      [mobile, settings],
    );

    const queryParamExist = useMemo(() => {
      if (category_ids && category_ids?.length > 0) {
        return true;
      }
    }, [category_ids]);

    const defaultExpanded = useMemo(
      () =>
        expandedSettings === "open-parent" ||
        expandedSettings === "open-parent-and-children",
      [expandedSettings],
    );

    const childrenDefaultExpanded = useMemo(
      () => expandedSettings === "open-parent-and-children",
      [expandedSettings],
    );

    return categories?.length || loadingCategoryList ? (
      <Filter
        ref={ref}
        alignment={settings.alignment}
        options={[{ values: categoriesParents?.map(categoryToOption) || [] }]}
        title={settings.title || "Categories"}
        queryParam="category_ids"
        variant={
          settings.position !== "vertical" ? "horizontal" : settings.position
        }
        mobile={mobile}
        defaultExpanded={queryParamExist || defaultExpanded}
        childrenDefaultExpanded={queryParamExist || childrenDefaultExpanded}
        loading={loadingCategoryList}
        horizontalVariant={settings.horizontalVariant}
      />
    ) : null;
  },
);

export default CategoriesFilter;
